import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, model, output, SimpleChanges, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogService } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { PaginatorModule } from 'primeng/paginator';
import { Popover } from 'primeng/popover';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { Table } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, Subject, switchMap, take } from 'rxjs';
import { BtnConfig } from 'src/app/models/general';
import { IamActionLabels, IamActionValues } from 'src/app/models/permission';
import { Column, PaginationData, TableInfoEdit } from 'src/app/models/table';
import { StatusClassPipe } from 'src/app/pipes/status-class/status-class-pipe';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { TranslationService } from 'src/app/services/configuration/translation-service';
import { DateTimeService } from 'src/app/services/date-time/date-time-service';
import { PermissionService } from 'src/app/services/user/permission-service';
import { AppButton } from '../../button/app-button';
import { ConfirmationDialog } from '../../confirmation-dialog/confirmation-dialog';
import { AppFilter } from '../../filter/filter';
import { TableCustomizePopup } from '../../table-customize-popup/table-customize-popup';

@Component({
  selector: 'app-table-edit-v2',
  imports: [
    CommonModule,
    AppFilter,
    AppButton,
    TableCustomizePopup,
    ReactiveFormsModule,
    TranslationPipe,
    FormsModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    CheckboxModule,
    MenuModule,
    TooltipModule,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
    InputNumberModule,
    InputGroupModule,
    TagModule,
    PaginatorModule,
    ProgressBarModule,
    StatusClassPipe
  ],
  templateUrl: './app-table-edit-v2.html',
  styleUrl: './app-table-edit-v2.scss'
})
export class AppTableEditV2 {
  private permissionService = inject(PermissionService);
  private route = inject(ActivatedRoute);
  private translationService = inject(TranslationService);
  private dateTimeService = inject(DateTimeService);
  private dialogService = inject(DialogService);

  @ViewChild('table') table!: Table;
  @ViewChild('filter') filter!: Popover;
  @ViewChild('customize') customize!: TableCustomizePopup;
  @ViewChild('moreMenu') moreMenu!: Popover;
  @ViewChild('pageInput') pageInput!: any;

  onParentCheckboxToggle = output<{ item: any; checked: boolean }>();
  onInlineEdit = output<void>();
  onInlineCancel = output<void>();
  onMoreItemClick = output<{ item: any; action: string }>();
  onIconClick = output<any>(); //replaced onEditClick
  onFieldClick = output<{ column: Column; rowIndex: number; formGroup: FormGroup; formControl: FormControl }>();
  onFooterBtnClick = output<any>();
  filterApply = output<any[]>();
  filterReset = output<void>();
  onPageChange = output<PaginationData>();
  headerBtnClick = output<string>();

  tableInfo = input<TableInfoEdit>(new TableInfoEdit());
  headerBtns = input<BtnConfig[]>([]);
  formArray = input.required<FormArray>();
  oriData = input<any[]>([]);
  mode = input<'add' | 'edit' | 'view'>('view');
  level = input<number>(1);
  parentItem = input<any>({});
  onInlineSave = input<(payload: { row: any; value: any; index: number }) => Promise<any>>();
  filterBtn = input<boolean>(true);
  filterFields = input<any[]>([]);
  dateField = input<boolean>(true);
  timeField = input<boolean>(true);
  filterData = input<any[]>([]);
  customTime = input<string>();
  dateTargetField = input<boolean>(true);
  search = input<boolean>(true);
  showConcurrentUsers = input<boolean>(false);

  checkboxStateMapProMax = model<{ [id: number]: any }>({});

  columnWidths: number[] = [];
  rowsPerPageOptions = [10, 50, 100];

  editingRow: {
    index: number | null;
    id: number | null;
    obj: any | null;
  } = { index: null, id: null, obj: null };
  isEditingAll: boolean = false;
  moduleCode: string | null = null;
  isEditAllowed: boolean = false; //for inline edit table
  activeMoreMenu: any | null = null;
  initializedRows = new Set<any>();
  doneLoading: boolean = false;
  searchValueCache: string = '';
  fieldChangeSubject = new Subject<{ column: Column; row: FormGroup; ri: number }>();
  cancelDialogEvent$ = new Subject<boolean>();

  constructor() {
    toObservable(this.formArray)
      .pipe(
        switchMap((fa) => fa.valueChanges),
        debounceTime(0)
      )
      .subscribe((fa: any[]) => {
        const hasChanges = this.checkIfPageEmpty(fa);
        if (hasChanges) {
          this.onCustomPagePrevious();
        }
      });
  }

  ngOnInit() {
    this.moduleCode = this.route.snapshot.data['moduleCode'];
    this.checkPermission();

    this.fieldChangeSubject.pipe(debounceTime(500)).subscribe(({ column, row, ri }) => {
      this.onFieldChangeEvent(column, row, ri);
    });
  }

  ngOnChanges(changes: SimpleChanges): void { }

  //region Scrolling
  isDragging = false;
  startX = 0;
  scrollLeft = 0;
  private container!: HTMLElement;
  private startOffset = 0;

  private isInteractiveElement(target: HTMLElement): boolean {
    return !!target.closest(`
    input,
    textarea,
    select,
    button,
    a,
    [contenteditable="true"],
    [role="button"],
    [role="combobox"],
    [role="checkbox"],
    [role="switch"],
    [role="slider"],
    [role="spinbutton"],
    [role="option"],
    [role="menuitem"],
    [tabindex]:not([tabindex="-1"])
  `);
  }

  startDrag(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.isInteractiveElement(target)) {
      return;
    }

    this.container = event.currentTarget as HTMLElement;
    this.isDragging = true;
    this.startX = event.pageX - this.container.offsetLeft;
    this.scrollLeft = this.container.scrollLeft;
  }

  onDrag(event: MouseEvent) {
    if (!this.isDragging) return;

    event.preventDefault();

    const walk = (event.pageX - this.startX) * 1.5;

    this.container.scrollLeft = this.scrollLeft - walk;
  }

  stopDrag() {
    this.isDragging = false;
  }
  //endregion Scrolling

  async checkPermission() {
    if (this.moduleCode) this.isEditAllowed = await this.permissionService.hasPermission(this.moduleCode, IamActionValues['EDIT']);
  }

  //#region Helpers
  getSortedBtns() {
    return [...this.headerBtns()].sort((a, b) => {
      // bigger number = more to the left
      // smaller number = more to the right
      return b.sequence - a.sequence;
    });
  }

  // #region Form Helpers
  getRows(): FormGroup[] {
    return this.formArray().controls as FormGroup[];
  }

  getControl(row: FormGroup, key: string): FormControl {
    return row.get(key) as FormControl;
  }

  startEdit(row: any, rowIndex: number) {
    this.onInlineEdit.emit();

    this.editingRow = {
      index: rowIndex,
      id: row.id,
      obj: row
    };
  }

  cancelEdit(index: number) {
    this.onInlineCancel.emit();

    this.formArray().at(index).reset(this.editingRow.obj);
    this.editingRow = {
      id: null,
      index: null,
      obj: null
    };
  }

  async saveEdit(row: FormGroup, index: number) {
    const value = this.formArray().at(index).value;

    try {
      await this.onInlineSave()!({
        row,
        value,
        index
      });

      this.editingRow = {
        id: null,
        index: null,
        obj: null
      };
    } catch {
      // stay in edit mode
    }
  }

  isColumnEditable(column: Column, row: FormGroup): boolean {
    if (!column.inlineEditConfigV2 || column.inlineEditConfigV2.editOn === 'off') return false;

    if (column.inlineEditConfigV2.editOn === 'condition') {
      const expectedValue = column.inlineEditConfigV2.conditionalEditValue ?? true; // fallback to true
      const allowEdit = row.value[column.inlineEditConfigV2.conditionalEditProp!] === expectedValue;
      row.value[column.id] = allowEdit ? '' : '-';
      return allowEdit;
    }

    return column.inlineEditConfigV2.editOn === 'default' ? true : false;
  }

  showEditCell(column: Column, row: FormGroup, rowIndex: number): boolean {
    if (!this.isColumnEditable(column, row)) return false;
    if (this.tableInfo().editType === 'row-based') return this.editingRow.index === rowIndex;
    if (this.tableInfo().editType === 'mode-based') {
      if (this.mode() !== 'view') return true;
    }
    return false;
  }

  getDropdownOptions(column: Column, row: any, colIndex: number) {
    if (!column.inlineEditConfigV2) return;

    const result = column.inlineEditConfigV2.getDropdownOptions
      ? column.inlineEditConfigV2.getDropdownOptions(row.value)
      : (column.inlineEditConfigV2.dropdownOptions ?? []);

    return result;
  }

  buildHeaderParams(col: Column) {
    if (!col.headerTranslationParams || col.headerTranslationParams.length === 0) {
      return {}; // no params -> translator works normally
    }

    // Convert ["MYR", "year"] to {0: "MYR", 1: "year"}
    return col.headerTranslationParams.reduce((acc, val, index) => {
      acc[index] = val;
      return acc;
    }, {} as any);
  }

  isRowEditing(item: any) {
    if (this.tableInfo().showIcon === 'edit' && this.isEditAllowed) {
      if ((this.tableInfo().editType === 'row-based' && this.editingRow.id === item.id) || this.isEditingAll) {
        return true;
      }
    }
    return false;
  }

  isRowDeletable(row: FormGroup) {
    if (this.tableInfo().showIcon !== 'delete') return false;
    if (this.tableInfo().editType === 'mode-based' && this.mode() === 'view') return false;

    if (this.tableInfo().deleteOn === 'condition') {
      const expectedValue = this.tableInfo().conditionalDeleteValue ?? true; // fallback to true
      const allowDelete = row.value[this.tableInfo().conditionalDeleteProp!] === expectedValue;

      return allowDelete;
    }

    return this.tableInfo().deleteOn === 'default' ? true : false;
  }

  //#region Checkbox
  checkboxCount = computed(() => {
    return Object.keys(this.checkboxStateMapProMax() ?? {}).length;
  });
  someChecked = computed(() => {
    return Object.keys(this.checkboxStateMapProMax()).length > 0;
  });
  someCheckedInternal = computed(() => {
    const target = this.checkboxStateMapProMax();
    const checkboxStateMapIds = Object.keys(target);
    const currentPageIds = this.formArray().getRawValue().map((d: any) => d[this.tableInfo().checkboxStateMapProp]);
    const someChecked = currentPageIds.some((id: any) =>
      checkboxStateMapIds.includes(String(id))
    );

    return someChecked;
  });

  private updateActiveMap(id: number, value: boolean, row?: any) {
    this.checkboxStateMapProMax.update((map) => {
      const newMap = { ...map };
      if (value === true) newMap[id] = this.buildMeta(row);
      else delete newMap[id];
      return newMap;
    });
  }

  private buildMeta(row: any) {
    if (this.tableInfo().checkboxProMaxProps?.length === 0) this.tableInfo().checkboxProMaxProps = ['id'];

    return (
      this.tableInfo().checkboxProMaxProps?.reduce((acc, prop) => {
        acc[prop] = row?.[prop];
        return acc;
      }, {} as any) ?? {}
    );
  }

  //combined function to handle both single checkbox and "Select All" checkbox
  onCheckboxToggle(row: any, isBulk: boolean = false, checked?: boolean, event?: any) {
    let checkedValue = checked;
    const rowObj = row ? row.value : null;

    const dataMap = new Map(this.formArray().controls.map((ctrl) => [ctrl.value[this.tableInfo().checkboxStateMapProp], ctrl.value]));

    const updateRow = (rowObj: any, value: boolean) => {
      if (rowObj.checkboxDefaultValue && rowObj.checkboxDefaultValue === true) return;

      this.updateActiveMap(rowObj[this.tableInfo().checkboxStateMapProp], value, rowObj);

      const dataItem = dataMap.get(rowObj[this.tableInfo().checkboxStateMapProp]);
      // if (dataItem) dataItem.checked = value; //not sure what this does

      if (this.tableInfo().type === 'nested') {
        const isParent = dataItem && dataItem[this.tableInfo().expandableProp];
        if (isParent) {
          const isExpanded = this.table.isRowExpanded(dataItem);

          if (!isExpanded && checkedValue) {
            this.table.toggleRow(dataItem);
            this.emitParentCheckboxToggle(dataItem, checkedValue);
          } else if (isExpanded && dataItem.value[this.tableInfo().nestedChildProp].length) {
            dataItem.value[this.tableInfo().nestedChildProp].forEach((child: any) => updateRow(child, checkedValue!));
          }
        }
      }
    };

    // --- CASE 1: Bulk (Select All) ---
    if (isBulk) {
      checkedValue = event.checked;
      this.formArray().controls.forEach((ctrl) => updateRow(ctrl.value, checkedValue!));
      return;
    }

    // --- CASE 2: Single Checkbox ---
    updateRow(rowObj, checkedValue!);
    return;
  }

  getCheckboxState(id: number): boolean {
    return (this.someChecked() && id in this.checkboxStateMapProMax()) ?? false;
  }

  // Function to handle "Select All" checkbox change
  onCheckboxAllChange(event: any) {
    this.onCheckboxToggle(null, true, event.checked, event);
  }

  emitParentCheckboxToggle(item: any, checked: boolean) {
    this.onParentCheckboxToggle.emit({ item, checked });
    // this.emitExpand(item);
  }
  //#endregion Checkbox

  //#region Action Buttons
  onHeaderBtnClickEvent(key: string) {
    if (key === 'cancel') {
      this.checkboxStateMapProMax.set({});
    }
    this.headerBtnClick.emit(key);
  }

  async onMoreClickEvent(event: MouseEvent, row: any, rowIndex: number, menu: Menu) {
    event.stopPropagation();

    let moreOptions: any;

    if (this.tableInfo().moreOptionsType === 'item-based') {
      moreOptions = row.moreOptions;
    } else {
      moreOptions = this.tableInfo().moreOptions;
    }

    // close previously opened menu
    if (this.activeMoreMenu && this.activeMoreMenu !== menu) {
      this.activeMoreMenu.hide();
    }

    moreOptions = (
      await Promise.all(
        moreOptions.map(async (option: any) => {
          if (option.separator) {
            return option;
          }

          option.value = option.value ?? option.label;

          const label = option.label ?? '';
          const required = this.permissionService.getTypeOfPermissionBasedOnLabel(label);

          if (!required) {
            return option;
          }

          const allowed = this.moduleCode
            ? await this.permissionService.hasPermission(
              this.moduleCode,
              required
            )
            : false;

          return allowed ? option : null;
        })
      )
    ).filter((option: any) => option !== null)
      .map((option: any) => {
        if (option.separator) {
          return { separator: true };
        }

        const label = option.label ?? '';

        return {
          ...option,
          disabled:
            row.disableMenuOption === true || option.disabled === true,
          label: this.translationService.translate(label),
          command: () => {
            this.onMoreItemClick.emit({
              item: row,
              action: label //not sure why different from app-table
            });

            if (label.toLowerCase() === 'edit') {
              this.startEdit(row, rowIndex);
            }

            // auto-close after click
            menu.hide();
            this.activeMoreMenu = null;
          }
        };
      });

    const moreOptionsEmpty = moreOptions.length === 0 || (moreOptions.every((m: any) => m.separator === true));
    if (moreOptionsEmpty) {
      row.moreOptions = [{
        disabled: true,
        label: this.translationService.translate('NO_PERMISSION'),
      }];
    }
    else {
      const shouldRemoveSeparator = moreOptions.length === 2 && moreOptions.some((m: any) => m.separator === true)
      row.moreOptions = shouldRemoveSeparator ? moreOptions.filter((m: any) => m.separator !== true) : moreOptions;

    }

    // toggle current menu
    menu.toggle(event);
    this.activeMoreMenu = menu;
  }

  onIconClickEvent(row: any, isChild: boolean = false) {
    let obj;

    if (!isChild && this.level() && this.parentItem()) {
      obj = { item: row, level: this.level(), parentId: this.parentItem().id };
    } else {
      obj = row;
    }

    this.onIconClick.emit(obj);
  }

  //#region Event Emitters
  onFieldChangeEvent(column: Column, row: FormGroup, ri: number) {
    const formControl = this.getControl(row, column.id);
    this.onFieldClick.emit({
      column,
      rowIndex: ri,
      formGroup: this.formArray().at(ri) as FormGroup,
      formControl
    });
  }

  onFieldChangeDebounceEvent(column: Column, row: FormGroup, ri: number) {
    const formControl = this.getControl(row, column.id);

    if (column.inlineEditConfigV2?.fieldType === 'number') {
      formControl.setValue(+formControl?.value);
    }

    this.fieldChangeSubject.next({ column, row, ri });
  }

  //#region Date
  formatDateRange(date: any) {
    if (!date) return null;
    const dateStr = this.dateTimeService.utcFormat(date);
    const [year, month, day] = dateStr!.split('T')[0].split('-');
    return `${day}-${month}-${year}`;
  }

  //#region Footer
  onFooterBtn(event: any) {
    const btnKey = event.btnKey ? event.btnKey : event;
    const parentItem = event.parentItem ? event.parentItem : this.parentItem();
    this.onFooterBtnClick.emit({ btnKey: btnKey, parentItem: parentItem });
  }

  //#region Customize
  onCustomize() {
    setTimeout(() => {
      this.measureColumnWidth();
    }, 0);
  }
  //#endregion output onclick events

  toggleCustomize(event: any) {
    this.customize.toggle(event);
  }

  //#region Customize
  toggleFilter(event: any) {
    this.filter.toggle(event);
  }

  handleFilterApply(filters: any[]) {
    this.filterApply.emit(filters);
  }

  handleFilterReset() {
    this.filterReset.emit();
  }

  setDoneLoading(event: boolean) {
    this.doneLoading = event;
  }

  //#region Rendering
  measureColumnWidth() {
    if (!this.table) return;

    const headers = this.table.el.nativeElement.querySelectorAll('thead th');

    let colIndex = 0;
    this.columnWidths = Array.from(headers as HTMLElement[])
      .filter((_, index: number) => {
        // let skipCount = 0;
        // skipCount += this.tableInfo().type === 'nested-alt' || this.tableInfo().type === 'nested-alt-child' ? 1 : 0; // if action container is shown, skip column
        // skipCount += this.tableInfo().showCheckbox || this.tableInfo().showIcon !== 'none' || this.tableInfo().editOn === 'clickBtn' ? 1 : 0; // if true, skip column

        // if (index > skipCount && index <= this.tableInfo().columns.length + skipCount) {
        const col = this.tableInfo().columns[colIndex];
        return !(col.hide === true || col.customizeHide === true);
        // }
        colIndex++;
      })
      .map((th: HTMLElement) => th.offsetWidth);
  }

  //#region Pagination
  onSearchChange() {
    if (this.tableInfo().paginationData.searchValue === '') this.onCustomPageChange();
  }

  clearSearch() {
    this.tableInfo().paginationData.searchValue = '';
    this.onCustomPageChange();
  }

  // Function to handle pagination change
  onCustomPageChange(newCurrentPage?: number, navigateTo: 'none' | 'prev' | 'next' = 'none') {
    if (this.tableInfo().showCancelOnPageChange) {
      const pageEmpty = this.checkIfPageEmpty();
      if (!pageEmpty) {
        const currentModeEdit = this.mode() === 'edit';
        const dataEdited = JSON.stringify(this.formArray().value) !== JSON.stringify(this.oriData());
        const hasChanges = currentModeEdit && dataEdited;
        if (hasChanges) {
          this.onCancel(navigateTo);
          return;
        }
      }
    }

    // this.tableInfo().paginationData.currentPage = event.page + 1;
    // this.tableInfo().paginationData.pageSize = event.rows;
    this.searchValueCache = this.tableInfo().paginationData.searchValue;

    this.onPageChange.emit({
      searchValue: this.tableInfo().paginationData.searchValue,
      currentPage: newCurrentPage ? newCurrentPage : this.tableInfo().paginationData.currentPage,
      pageSize: this.tableInfo().paginationData.pageSize,
      totalPages: this.tableInfo().paginationData.totalPages,
      totalRecords: this.tableInfo().paginationData.totalRecords
    });
  }

  get startItem(): number {
    return this.tableInfo().paginationData.totalRecords === 0
      ? 0
      : (this.tableInfo().paginationData.currentPage - 1) * this.tableInfo().paginationData.pageSize + 1;
  }

  get endItem(): number {
    const end = this.tableInfo().paginationData.currentPage * this.tableInfo().paginationData.pageSize;
    return end > this.tableInfo().paginationData.totalRecords ? this.tableInfo().paginationData.totalRecords : end;
  }

  onCustomPageNext() {
    if (this.tableInfo().paginationData.currentPage < this.tableInfo().paginationData.totalPages) {
      const newCurrentPage = this.tableInfo().paginationData.currentPage + 1;
      this.onCustomPageChange(newCurrentPage, 'next');
    }
  }

  onCustomPagePrevious() {
    if (this.tableInfo().paginationData.currentPage > 1) {
      const newCurrentPage = this.tableInfo().paginationData.currentPage - 1;
      this.onCustomPageChange(newCurrentPage, 'prev');
    }
  }

  onPageNumberInputChange(input: any) {
    const newCurrentPage = input.value;
    if (this.tableInfo().paginationData.totalPages > 0) {
      if (newCurrentPage) {
        if (newCurrentPage > this.tableInfo().paginationData.totalPages) {
          this.tableInfo().paginationData.currentPage = this.tableInfo().paginationData.totalPages;
        } else if (newCurrentPage < 1) {
          this.tableInfo().paginationData.currentPage = 1;
        } else {
          this.tableInfo().paginationData.currentPage = newCurrentPage;
        }
      } else {
        this.tableInfo().paginationData.currentPage = this.tableInfo().paginationData.currentPage;
      }
    } else {
      this.tableInfo().paginationData.currentPage = 0;
    }

    input.value = this.tableInfo().paginationData.currentPage;
    this.onCustomPageChange();
  }

  onPageSizeChange(event: any) {
    this.tableInfo().paginationData.pageSize = event.value;
    this.onCustomPageChange();
  }

  checkIfPageEmpty(formArray?: any[]) {
    const fa = formArray ?? this.formArray();
    const currentPageEmpty =
      fa.length === 0 && this.tableInfo().paginationData.totalPages > 0 && this.tableInfo().paginationData.currentPage > 1;
    if (currentPageEmpty) {
      return true;
    }

    return false;
  }

  onCancel(navigateTo: 'none' | 'prev' | 'next') {
    this.cancelDialogEvent$ = new Subject<boolean>();

    const ref = this.dialogService.open(ConfirmationDialog, {
      header: this.translationService.translate('CANCEL_CHANGES'),
      data: {
        event$: this.cancelDialogEvent$,
        content1: this.translationService.translate('CANCEL_CHANGES_DESC'),
        content2: this.translationService.translate('CONTINUE_QUESTION'),
        primaryBtnLabel: this.translationService.translate('CONFIRM')
      },
      width: '37.5rem',
      modal: true
    });

    this.cancelDialogEvent$.pipe(take(1)).subscribe({
      next: (res) => {
        if (res === true && this.oriData()) {
          this.formArray().setValue(this.oriData());
          if (navigateTo === 'prev') {
            this.onCustomPagePrevious();
          } else if (navigateTo === 'next') {
            this.onCustomPageNext();
          } else {
            this.onCustomPageChange();
          }
        }

        ref.close();
      }
    });
  }
  //#endregion Pagination
}
